import React, { useEffect, useMemo, useState } from 'react';
import { Form, FormGroup, Label, Input, Modal, ModalHeader, ModalBody, ModalFooter, Button } from 'reactstrap';
import Layout from '../components/Layout';
import api from '../services/api';

const LeaguePayments = () => {
  const [leagues, setLeagues] = useState([]);
  const [selectedLeagueId, setSelectedLeagueId] = useState(null);
  const [leagueTeams, setLeagueTeams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [leaguesLoading, setLeaguesLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [playerToUncheck, setPlayerToUncheck] = useState(null);

  useEffect(() => {
    const fetchLeagues = async () => {
      try {
        const response = await api.get('/leagues/');
        const allLeagues = Array.isArray(response.data) ? response.data : [];
        const leagueList = allLeagues.filter((league) => {
          return league?.state === 'Active' || league?.state === 'Registration';
        });
        setLeagues(leagueList);
        
        // Auto-select first league if available
        if (leagueList.length > 0) {
          setSelectedLeagueId(leagueList[0].id);
        }
      } catch (error) {
        console.error('Error loading leagues:', error);
      } finally {
        setLeaguesLoading(false);
      }
    };

    fetchLeagues();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (!selectedLeagueId) return;
      
      setLoading(true);
      try {
        const teamsResponse = await api.get(`/league-teams/?league=${selectedLeagueId}`);
        setLeagueTeams(teamsResponse.data || []);
      } catch (error) {
        console.error('Error loading league players:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedLeagueId]);

  const players = useMemo(() => {
    const flattened = leagueTeams.flatMap((lt) =>
      (lt.players || []).map((player) => ({
        ...player,
        team_display: lt.team_display || 'Team',
      }))
    );

    return flattened.sort((a, b) => {
      const lastA = (a.last_name || '').toLowerCase();
      const lastB = (b.last_name || '').toLowerCase();
      if (lastA !== lastB) return lastA.localeCompare(lastB);

      const firstA = (a.first_name || '').toLowerCase();
      const firstB = (b.first_name || '').toLowerCase();
      if (firstA !== firstB) return firstA.localeCompare(firstB);

      const teamA = (a.team_display || '').toLowerCase();
      const teamB = (b.team_display || '').toLowerCase();
      return teamA.localeCompare(teamB);
    });
  }, [leagueTeams]);

  const fetchPlayers = async () => {
    if (!selectedLeagueId) return;
    try {
      const teamsResponse = await api.get(`/league-teams/?league=${selectedLeagueId}`);
      setLeagueTeams(teamsResponse.data || []);
    } catch (error) {
      console.error('Error loading league players:', error);
    }
  };

  const handlePaidStatusChange = async (player) => {
    // If unchecking (marking as unpaid), show confirmation modal
    if (player.paid) {
      setPlayerToUncheck(player);
      setModalOpen(true);
      return;
    }

    // If checking (marking as paid), proceed directly
    try {
      await api.put(`/team-players/${player.id}/`, {
        ...player,
        paid: !player.paid,
      });
      fetchPlayers();
    } catch (error) {
      console.error('Error updating player paid status:', error);
    }
  };

  const confirmUncheck = async () => {
    if (!playerToUncheck) return;

    try {
      await api.put(`/team-players/${playerToUncheck.id}/`, {
        ...playerToUncheck,
        paid: false,
      });
      fetchPlayers();
      setModalOpen(false);
      setPlayerToUncheck(null);
    } catch (error) {
      console.error('Error updating player paid status:', error);
      setModalOpen(false);
      setPlayerToUncheck(null);
    }
  };

  const cancelUncheck = () => {
    setModalOpen(false);
    setPlayerToUncheck(null);
  };

  return (
    <Layout>
      <div className="container my-4">
        <style>{`
          @media print {
            .print-button { display: none !important; }
            .print-container { padding: 0 !important; }
            body > * { margin: 0; padding: 0; }
            /* Hide Layout components except the main content */
            .col-sm-4, .nav, [class*="sidebar"], header, nav { display: none !important; }
            .container { width: 100% !important; max-width: 100% !important; margin: 0 !important; padding: 10px !important; }
          }
          .page-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 1.5rem;
          }
          .player-row {
            display: grid;
            grid-template-columns: 40px 1fr 1fr 1fr;
            gap: 12px;
            align-items: center;
            padding: 6px 0;
            border-bottom: 1px solid #e6e6e6;
          }
          .player-row:last-child { border-bottom: none; }
          .player-checkbox {
            width: 20px;
            height: 20px;
          }
        `}</style>

        <div className="print-container">
          <div className="page-header">
            <h2>League Payments</h2>
            <button type="button" className="btn btn-primary btn-sm print-button" onClick={() => window.print()}>
              Print
            </button>
          </div>

          {leaguesLoading && <p className="text-muted">Loading leagues...</p>}

          {!leaguesLoading && leagues.length === 0 && (
            <p className="text-muted">No active or registration leagues found.</p>
          )}

          {!leaguesLoading && leagues.length > 0 && (
            <>
              <Form className="mb-4">
                <FormGroup>
                  <Label for="league-select">Select League</Label>
                  <Input
                    id="league-select"
                    type="select"
                    value={selectedLeagueId || ''}
                    onChange={(e) => setSelectedLeagueId(Number(e.target.value))}
                  >
                    {leagues.map((l) => (
                      <option key={l.id} value={l.id}>
                        {l.title}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              </Form>

              {loading && <p className="text-muted">Loading players...</p>}

              {!loading && selectedLeagueId && players.length === 0 && (
                <p className="text-muted">No players found for this league.</p>
              )}

              {!loading && selectedLeagueId && players.length > 0 && (
                <div>
                  <div className="player-row fw-semibold">
                    <div>Paid</div>
                    <div>Name</div>
                    <div>Team</div>
                    <div>Email</div>
                  </div>
                  {players.map((player) => (
                    <div className="player-row" key={player.id}>
                      <input
                        type="checkbox"
                        className="player-checkbox"
                        checked={!!player.paid}
                        onChange={() => handlePaidStatusChange(player)}
                      />
                      <div>{player.last_name}, {player.first_name}</div>
                      <div>{player.team_display}</div>
                      <div>{player.email || '-'}</div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <Modal isOpen={modalOpen} toggle={cancelUncheck}>
        <ModalHeader toggle={cancelUncheck}>Confirm</ModalHeader>
        <ModalBody>
          {playerToUncheck && (
            <p>
              Are you sure you want to mark <strong>{playerToUncheck.first_name} {playerToUncheck.last_name}</strong> as unpaid?
            </p>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={cancelUncheck}>
            Cancel
          </Button>
          <Button color="danger" onClick={confirmUncheck}>
            Mark as Unpaid
          </Button>
        </ModalFooter>
      </Modal>
    </Layout>
  );
};

export default LeaguePayments;
